import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    untracked,
    viewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../../../main/type.map.service";
import { Adjustment, Invoice, LineItem } from "../../../../../pojo/Pricing";
import { S25Const } from "../../../../../util/s25-const";
import { Table } from "../../../../s25-table/Table";
import { S25TableModule } from "../../../../s25-table/s25.table.module";
import { S25TableComponent } from "../../../../s25-table/s25.table.component";
import { GenericTableRenderHTMLComponent } from "../../../../s25-table/generics/generic.table.render.html.component";
import { S25PricingUtil } from "../../../s25.pricing.util";
import { PricingStore } from "../../s25.event.pricing.store";
import { PricingColumns } from "../../pricing.columns";
import { InvoiceTotals } from "./invoice-totals";
/** One org's line on the totals table, before its gross adjustments are listed separately. */
type OrgRow = {
    orgId: number;
    orgName: string;
    listPrice: number;
    adjustments: number;
    price: number;
    tax: number;
    total: number;
    grandTotal: number;
    payments: number;
    balance: number;
};

const currency = S25PricingUtil.formatCurrency;

@TypeManagerDecorator("s25-ng-invoice-totals")
@Component({
    selector: "s25-ng-invoice-totals",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25TableModule],
    template: `
        @if (hasRows()) {
            <div class="pricing_header totals">Totals</div>
            <s25-ng-table [caption]="'Pricing Totals'" [dataSource]="tableConfig()" [unlimitedWidth]="true" />
        }
    `,
    styles: `
        :host ::ng-deep .tax-name {
            font-size: 90%;
        }

        :host ::ng-deep table {
            table-layout: fixed;
        }

        :host ::ng-deep s25-ng-table .rose-object-table-header {
            margin-bottom: 0;
        }

        /* Set the grand total apart from the rows it sums */
        :host ::ng-deep .grand-total-row {
            border-top: 2px solid #555;
        }

        :host ::ng-deep s25-ng-table table.s25-ng-table--table > tbody > tr.grand-total-row > td.s25-ng-table--cell {
            padding-top: 1em !important;
            font-weight: bold;
            vertical-align: top;
        }
    `,
})
export class S25InvoiceTotalsComponent {
    private readonly pricingStore = inject(PricingStore);

    /** Set where the totals belong to an invoice other than the selected one, as billed items do. */
    readonly invoice = input<Invoice | undefined>(undefined);

    readonly selectedInvoice = computed(() => this.invoice() ?? this.pricingStore.selectedInvoice());
    private readonly combineRelated = this.pricingStore.combineRelated;
    private readonly eventId = this.pricingStore.eventId;
    private readonly orgNameById = this.pricingStore.orgNameById;

    private readonly totalsTable = viewChild(S25TableComponent);

    readonly orgRows = computed<OrgRow[]>(() => {
        const invoice = this.selectedInvoice(),
            scope = { combineRelated: this.combineRelated(), eventId: this.eventId() },
            subtotals = InvoiceTotals.subtotalsByOrg(invoice, scope),
            invoiceSubtotals = InvoiceTotals.invoiceSubtotalByOrg(invoice),
            payments = InvoiceTotals.paymentsByOrg(invoice, scope),
            orgNameById = this.orgNameById();

        // An org can be paid, or carry a gross adjustment, without being charged anything here.
        const orgIds = new Set([
            ...subtotals.keys(),
            ...InvoiceTotals.adjustmentsByOrg(invoice).keys(),
            ...payments.keys(),
        ]);

        return [...orgIds]
            .map((orgId) => {
                const totals = InvoiceTotals.sumSubtotals(subtotals.get(orgId) ?? []),
                    invoiceSubtotal = invoiceSubtotals.get(orgId),
                    paid = payments.get(orgId) ?? 0;

                return {
                    orgId,
                    orgName: orgNameById.get(orgId) ?? S25Const.private,
                    listPrice: totals.listPrice,
                    adjustments: totals.profileAdjustments + totals.requirementsAdjustments,
                    price: totals.taxableAmount,
                    tax: totals.tax,
                    total: totals.eventsTotalCharge,
                    grandTotal: InvoiceTotals.orgGrandTotal(totals, invoiceSubtotal, scope.combineRelated),
                    payments: paid,
                    // Each gross adjustment carries its own charge on its own row, so this stays
                    // the charge the Subtotals row reports less what has been paid against it.
                    balance: S25PricingUtil.convertToDollars(
                        S25PricingUtil.convertToCents(totals.eventsTotalCharge) - S25PricingUtil.convertToCents(paid),
                    ),
                };
            })
            .sort((first, second) => first.orgName.localeCompare(second.orgName));
    });

    readonly adjustmentRows = computed(() => {
        const byOrg = InvoiceTotals.adjustmentsByOrg(this.selectedInvoice()),
            orgNameById = this.orgNameById();

        return this.orgRows().flatMap((org) =>
            (byOrg.get(org.orgId) ?? []).map((adjustment) => ({
                adjustment,
                orgName: orgNameById.get(org.orgId) ?? S25Const.private,
            })),
        );
    });

    readonly hasRows = computed(() => !!this.orgRows().length);

    readonly tableConfig = computed<Table.DataSource>(() => ({
        type: "unpaginated",
        dataSource: () => Promise.resolve({ rows: this.buildRows() }),
        columns: PricingColumns.pricingTotalsColumns(),
    }));

    constructor() {
        effect(() => {
            // Re-read rows in place on any pricing change rather than remounting the table.
            this.selectedInvoice();
            this.combineRelated();
            untracked(() => {
                this.totalsTable()?.refresh(false, false, true);
            });
        });
    }

    private buildRows(): Table.Row[] {
        return [
            ...this.orgRows().map((org) => this.subtotalRow(org)),
            ...this.adjustmentRows().map(({ adjustment, orgName }) => this.adjustmentRow(adjustment, orgName)),
            this.grandTotalRow(),
        ];
    }

    private subtotalRow(org: OrgRow): Table.Row {
        return {
            id: `totals-subtotal-${org.orgId}`,
            name: "Subtotals",
            cells: {
                item: { text: "Subtotals" },
                listPrice: { text: currency(org.listPrice) },
                adjust: { text: currency(org.adjustments) },
                price: { text: currency(org.price) },
                tax: { text: currency(org.tax) },
                total: { text: currency(org.total) },
                payments: { text: currency(org.payments) },
                balance: { text: currency(org.balance) },
                charge: { text: org.orgName },
            },
        };
    }

    private adjustmentRow(adjustment: Adjustment, orgName: string): Table.Row {
        const totalCharge = adjustment.totalCharge ?? 0;

        return {
            id: `totals-adjustment-${adjustment.chargeToId}-${adjustment.itemId}`,
            name: adjustment.adjustmentName ?? "Adjustment",
            cells: {
                item: { text: "Adjustment" },
                // The list price column carries the reason, as it does in old pricing
                listPrice: { text: adjustment.adjustmentName ?? "" },
                adjust: {
                    text: adjustment.adjustmentPercent
                        ? S25PricingUtil.formatPercent(adjustment.adjustmentPercent)
                        : currency(adjustment.adjustmentAmt ?? 0),
                },
                total: { text: currency(totalCharge) },
                balance: { text: currency(totalCharge) },
                charge: { text: orgName },
            },
        };
    }

    private grandTotalRow(): Table.Row {
        const orgRows = this.orgRows(),
            adjustmentTotal = this.adjustmentRows().reduce(
                (sum, { adjustment }) => sum + (adjustment.totalCharge ?? 0),
                0,
            );

        const sum = (amount: (org: OrgRow) => number) => orgRows.reduce((total, org) => total + amount(org), 0);

        return {
            id: "totals-grand-total",
            name: "Grand Total",
            className: "grand-total-row",
            cells: {
                item: { text: "Grand Total" },
                listPrice: { text: currency(sum((org) => org.listPrice)) },
                adjust: { text: currency(sum((org) => org.adjustments) + adjustmentTotal) },
                price: { text: currency(sum((org) => org.price)) },
                tax: this.taxCell(),
                total: { text: currency(sum((org) => org.grandTotal)) },
                payments: { text: currency(sum((org) => org.payments)) },
                balance: { text: currency(sum((org) => org.balance) + adjustmentTotal) },
                charge: { text: "" },
            },
        };
    }

    private taxCell(): Table.Cell {
        const taxes = InvoiceTotals.taxBreakdown(this.taxedLineItems());

        return {
            component: GenericTableRenderHTMLComponent,
            inputs: {
                text: taxes
                    .map(
                        ({ taxName, taxCharge }, index) =>
                            `<p class="tax-name">${taxName}:</p>
                             <p${index < taxes.length - 1 ? ' class="c-margin-bottom--quarter"' : ""}>${currency(taxCharge)}</p>`,
                    )
                    .join(""),
                isHtml: !!taxes.length,
            },
        };
    }

    private taxedLineItems(): LineItem[] {
        const lineItems = this.selectedInvoice()?.billing?.lineItems ?? [];
        if (this.combineRelated()) return lineItems;
        return lineItems.filter((lineItem) => lineItem.eventId === this.eventId());
    }
}
